import clsx from 'clsx'
import React from 'react'

import { Button } from '../Button'
import { Link } from '../Link'

/** Solid button colors matching Button component. 'secondary' is alias for outline style. */
export type CardActionVariant =
  | 'primary'
  | 'secondary'
  | 'dark/gray'
  | 'light'
  | 'dark/white'
  | 'dark'
  | 'white'
  | 'gray'
  | 'indigo'
  | 'cyan'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'sky'
  | 'blue'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'outline'
  | 'plain'

export type CardAction = {
  label: string
  onClick: () => void
  variant?: CardActionVariant
  className?: string
}

export type CardLayout = 'vertical' | 'horizontal'
export type CardImageLayout = 'object-cover' | 'object-contain'

export type CardProps = {
  title: string
  description: string
  icon?: React.ReactNode
  imageSrc?: string
  // Defaults to decorative (`""`). Pass text when the image is informative
  imageAlt?: string
  layout?: CardLayout
  imageLayout?: CardImageLayout
  // Without actions: whole card links. With actions: title links only
  link?: string
  actions?: CardAction[]
  children?: React.ReactNode
  className?: string
  /** Optional badge overlay rendered in the top-right of the card. */
  badge?: React.ReactNode
  disabled?: boolean
}

export const Card = ({
  title,
  description,
  icon,
  imageSrc,
  imageAlt = '',
  layout,
  imageLayout = 'object-cover',
  link,
  actions = [],
  children,
  className,
  badge,
  disabled = false,
}: CardProps) => {
  const hasImage = Boolean(imageSrc)
  const isHorizontal = layout === 'horizontal'
  const hasActions = actions.length > 0
  const isFullyLinked = Boolean(link) && !hasActions && !disabled

  const textBlock = (
    <>
      <div className="flex items-start gap-2">
        {icon && (
          <span
            aria-hidden="true"
            className="shrink-0 text-gray-500 dark:text-gray-400 [&>svg]:size-5"
          >
            {icon}
          </span>
        )}
        {link && hasActions ? (
          <Link
            to={link}
            className="font-semibold text-base sm:text-sm hover:underline focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500 text-gray-900 dark:text-white"
          >
            {title}
          </Link>
        ) : (
          <h3 className="font-semibold text-base sm:text-sm text-gray-900 dark:text-white">{title}</h3>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</p>
      {children && <div className="mt-3">{children}</div>}
    </>
  )

  const renderActionButton = (action: CardAction, index: number) => {
    const v = action.variant ?? 'secondary'
    if (v === 'secondary' || v === 'outline') {
      return (
        <Button
          key={index}
          outline
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )
    }
    if (v === 'plain') {
      return (
        <Button
          key={index}
          plain
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )
    }
    return (
      <Button
        key={index}
        color={v}
        onClick={action.onClick}
      >
        {action.label}
      </Button>
    )
  }

  const actionsBlock = hasActions ? (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      {actions.map((action, index) => renderActionButton(action, index))}
    </div>
  ) : null

  const shellClassName = clsx(
    'relative flex flex-col h-full rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
    className,
    !disabled && link ? 'transition-colors hover:bg-gray-50 dark:hover:bg-gray-700' : '',
    disabled && 'cursor-not-allowed opacity-50',
    isFullyLinked &&
      'cursor-pointer focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500',
  )

  const body = (
    <>
      {badge && <div className="absolute top-3 right-3">{badge}</div>}
      <div
        className={clsx(
          'flex gap-4 p-4 sm:p-5 flex-1 min-h-0',
          isHorizontal && !hasImage
            ? 'flex-row items-center'
            : hasImage && isHorizontal
              ? 'flex-row items-start'
              : 'flex-col',
        )}
      >
        {hasImage && (
          <div className={clsx('shrink-0 overflow-hidden rounded-md', isHorizontal ? 'size-24' : 'h-24 w-auto')}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className={`h-full w-full ${imageLayout}`}
            />
          </div>
        )}
        {isHorizontal && !hasImage ? (
          <>
            <div className="min-w-0 flex-1">{textBlock}</div>
            {actionsBlock}
          </>
        ) : (
          <div className="flex flex-col min-w-0 flex-1 min-h-0">
            <div className="min-h-0 flex-1">{textBlock}</div>
            {actionsBlock && <div className="mt-auto pt-3">{actionsBlock}</div>}
          </div>
        )}
      </div>
    </>
  )

  if (isFullyLinked) {
    return (
      <Link
        to={link!}
        className={shellClassName}
      >
        {body}
      </Link>
    )
  }

  return (
    <div
      className={shellClassName}
      aria-disabled={disabled || undefined}
    >
      {body}
    </div>
  )
}
